import backendV2Service from './backend-v2.service';

// ---------------------------------------------------------------------------
// Audiencia: el eje único de la pantalla (KB-693).
//
// Las tres audiencias caen sobre DOS sistemas de encuesta distintos:
//   STUDENTS    -> Smileyometer 1-5 (tablas student_satisfaction_*)
//   TEACHERS    -> NPS 0-10 (nps_* con app = TEACHERS_WEB)
//   DATA_SYSTEM -> NPS 0-10 (nps_* con app = DATA_SYSTEM)
//
// El backend segmenta el NPS por `app`, NO por el rol del usuario (decisión de
// KB-691: los admins de Data System no tienen colegio y el rol no alcanzaba).
// ---------------------------------------------------------------------------

export type Audience = 'STUDENTS' | 'TEACHERS' | 'DATA_SYSTEM';

export const AUDIENCES: Audience[] = ['STUDENTS', 'TEACHERS', 'DATA_SYSTEM'];

/** La audiencia viaja en la URL en kebab-case. */
const AUDIENCE_SLUG: Record<Audience, string> = {
  STUDENTS: 'students',
  TEACHERS: 'teachers',
  DATA_SYSTEM: 'data-system',
};

export const isNpsAudience = (audience: Audience): boolean => audience !== 'STUDENTS';

export type CampaignStatus = 'ACTIVE' | 'SCHEDULED' | 'FINISHED' | 'INACTIVE';

export interface Campaign {
  id: number;
  audience: Audience;
  name: string;
  startsAt: string | null; // YYYY-MM-DD
  endsAt: string | null;
  status: CampaignStatus;
  responseCount: number;
  active: boolean;
  countryIds: number[]; // [] = todos los países (KB-778)
}

export interface CampaignPayload {
  name: string;
  startsAt?: string | null;
  endsAt?: string | null;
  countryIds?: number[];
}

// --- Resultados de NPS -----------------------------------------------------

export interface NpsAggregate {
  n: number;
  npsScore: number; // %promotores − %detractores, −100..100
  promoters: number; // 9-10
  passives: number; // 7-8
  detractors: number; // 0-6
  promotersPct: number;
  passivesPct: number;
  detractorsPct: number;
  averageScore: number; // 0..10
  distribution: Record<string, number>; // claves '0'..'10'
}

export interface NpsSchoolCut extends NpsAggregate {
  schoolId: number | null;
  school: string | null;
}

export interface NpsCountryCut extends NpsAggregate {
  country: string | null;
}

export interface NpsResults {
  campaignId: number;
  global: NpsAggregate;
  bySchool: NpsSchoolCut[];
  byCountry: NpsCountryCut[];
  commentsCount: number;
}

export interface NpsComment {
  id: number;
  userName: string | null;
  userLastname: string | null;
  npsScore: number;
  comment: string | null;
  schoolName: string | null;
  country: string | null;
  createdAt: string;
}

export interface NpsCommentsPage {
  items: NpsComment[];
  total: number;
  page: number;
  pageSize: number;
}

export interface NpsFilters {
  country?: string | null;
  schoolId?: number | null;
  negativeOnly?: boolean;
}

// --- Resultados de satisfacción (los que ya devolvía KB-692) ----------------

export interface SatisfactionScoreBucket {
  score: number; // 1..5
  count: number;
  percentage: number;
}

export interface KeepReadingSplit {
  yes: number;
  maybe: number;
  no: number;
  yesPercentage: number;
  maybePercentage: number;
  noPercentage: number;
}

export interface SatisfactionBreakdownRow {
  label: string;
  n: number;
  topTwoBox: number;
  averageScore: number;
  countryId?: number | null;
}

export interface SatisfactionResults {
  campaignId: number;
  n: number;
  topTwoBox: number;
  averageScore: number;
  scoreDistribution: SatisfactionScoreBucket[];
  keepReading: KeepReadingSplit;
  byGrade: SatisfactionBreakdownRow[];
  bySchool: SatisfactionBreakdownRow[];
  byCountry: SatisfactionBreakdownRow[];
}

// ---------------------------------------------------------------------------
// Mapeo de la respuesta cruda de satisfacción a la forma de la vista.
// Es el mismo mapeo que usaba la vista vieja de satisfacción (KB-692): el
// backend devuelve `distribution` como un OBJETO de conteos por score ('1'..'5')
// y `againAgain` como conteos YES/MAYBE/NO, no porcentajes. No hay averageScore
// ni scoreDistribution[] en el cable: son conceptos de la vista.
// ---------------------------------------------------------------------------

interface BackendSatisfactionAggregate {
  n: number;
  top2BoxPct: number;
  distribution: Record<string, number>;
  againAgain: Record<string, number>;
}

interface BackendSatisfactionResults {
  campaignId: number;
  global: BackendSatisfactionAggregate;
  byGrade: Array<BackendSatisfactionAggregate & { grade: string }>;
  bySchool: Array<BackendSatisfactionAggregate & { school: string }>;
  byCountry: Array<BackendSatisfactionAggregate & { country: string; countryId: number | null }>;
}

const SCORE_KEYS = ['1', '2', '3', '4', '5'];

const computeAverageScore = (distribution: Record<string, number>, n: number): number => {
  if (!n) return 0;
  const sum = SCORE_KEYS.reduce(
    (acc, key) => acc + Number(key) * (distribution[key] || 0),
    0,
  );
  return sum / n;
};

const mapScoreDistribution = (
  distribution: Record<string, number>,
  n: number,
): SatisfactionScoreBucket[] => SCORE_KEYS.map((key) => {
  const count = distribution[key] || 0;
  return { score: Number(key), count, percentage: n > 0 ? (count / n) * 100 : 0 };
});

/**
 * humps.camelize fuerza a minúscula el primer carácter de TODA clave que
 * procesa, incluso sin guiones bajos que convertir: { YES, MAYBE, NO } vuelve
 * como { yES, mAYBE, nO }. No dependemos de ninguna de las dos grafías.
 */
const normalizeCounts = (counts: Record<string, number>) => {
  const normalized = { YES: 0, MAYBE: 0, NO: 0 };
  Object.keys(counts || {}).forEach((key) => {
    const upperKey = key.toUpperCase() as 'YES' | 'MAYBE' | 'NO';
    if (upperKey in normalized) normalized[upperKey] = counts[key] || 0;
  });
  return normalized;
};

const mapKeepReading = (counts: Record<string, number>): KeepReadingSplit => {
  const { YES, MAYBE, NO } = normalizeCounts(counts);
  const total = YES + MAYBE + NO;
  const toPercentage = (value: number) => (total > 0 ? (value / total) * 100 : 0);
  return {
    yes: YES,
    maybe: MAYBE,
    no: NO,
    yesPercentage: toPercentage(YES),
    maybePercentage: toPercentage(MAYBE),
    noPercentage: toPercentage(NO),
  };
};

const mapBreakdownRow = (
  label: string,
  aggregate: BackendSatisfactionAggregate,
  countryId?: number | null,
): SatisfactionBreakdownRow => ({
  label,
  n: aggregate.n,
  topTwoBox: aggregate.top2BoxPct,
  averageScore: computeAverageScore(aggregate.distribution, aggregate.n),
  countryId,
});

const mapSatisfactionResults = (
  backendResults: BackendSatisfactionResults,
): SatisfactionResults => {
  const globalAggregate = backendResults.global;
  return {
    campaignId: backendResults.campaignId,
    n: globalAggregate.n,
    topTwoBox: globalAggregate.top2BoxPct,
    averageScore: computeAverageScore(globalAggregate.distribution, globalAggregate.n),
    scoreDistribution: mapScoreDistribution(globalAggregate.distribution, globalAggregate.n),
    keepReading: mapKeepReading(globalAggregate.againAgain),
    byGrade: (backendResults.byGrade || []).map((row) => mapBreakdownRow(row.grade, row)),
    bySchool: (backendResults.bySchool || []).map((row) => mapBreakdownRow(row.school, row)),
    byCountry: (backendResults.byCountry || [])
      .map((row) => mapBreakdownRow(row.country, row, row.countryId)),
  };
};

// ---------------------------------------------------------------------------

const unwrap = <T>(req: Promise<{ data: T }>) => req.then(({ data }) => data);

const base = (audience: Audience) => `/surveys/${AUDIENCE_SLUG[audience]}/campaigns`;

/**
 * OJO: axios `transformRequest` decameliza el BODY, pero NO los query params.
 * Los params de los GET van en snake_case a mano (`school_id`, `page_size`), o
 * el ValidationPipe del backend (whitelist: true) los descarta en silencio.
 */
const npsParams = (filters: NpsFilters) => ({
  ...(filters.country ? { country: filters.country } : {}),
  ...(filters.schoolId ? { school_id: filters.schoolId } : {}),
  // Nunca se manda `negative_only=false`: se omite el parámetro cuando el
  // filtro está apagado (el backend interpretaría el string "false" como
  // truthy — ver el comentario en CommentsQueryDto del backend).
  ...(filters.negativeOnly ? { negative_only: true } : {}),
});

export default {
  listCampaigns: (audience: Audience): Promise<Campaign[]> => unwrap(
    backendV2Service.get<Campaign[]>(base(audience)),
  ),

  createCampaign: (audience: Audience, payload: CampaignPayload): Promise<Campaign> => unwrap(
    backendV2Service.post<Campaign>(base(audience), payload),
  ),

  updateCampaign: (
    audience: Audience,
    id: number,
    payload: Partial<CampaignPayload>,
  ): Promise<Campaign> => unwrap(
    backendV2Service.patch<Campaign>(`${base(audience)}/${id}`, payload),
  ),

  // Reusa el mismo PATCH que updateCampaign: active es un campo más del
  // recurso, no una acción separada del lado del backend.
  toggleCampaignActive: (
    audience: Audience,
    id: number,
    active: boolean,
  ): Promise<Campaign> => unwrap(
    backendV2Service.patch<Campaign>(`${base(audience)}/${id}`, { active }),
  ),

  deleteCampaign: (audience: Audience, id: number): Promise<void> => unwrap(
    backendV2Service.delete<void>(`${base(audience)}/${id}`),
  ),

  getNpsResults: (
    audience: Audience,
    campaignId: number,
    filters: NpsFilters = {},
  ): Promise<NpsResults> => unwrap(
    backendV2Service.get<NpsResults>(`${base(audience)}/${campaignId}/results`, {
      params: npsParams(filters),
    }),
  ),

  getNpsComments: (
    audience: Audience,
    campaignId: number,
    page: number,
    pageSize: number,
    filters: NpsFilters = {},
  ): Promise<NpsCommentsPage> => unwrap(
    backendV2Service.get<NpsCommentsPage>(`${base(audience)}/${campaignId}/comments`, {
      params: { page, page_size: pageSize, ...npsParams(filters) },
    }),
  ),

  getSatisfactionResults: (
    campaignId: number,
    countryId?: number | null,
  ): Promise<SatisfactionResults> => unwrap(
    backendV2Service.get<BackendSatisfactionResults>(
      `${base('STUDENTS')}/${campaignId}/results`,
      { params: countryId ? { country_id: countryId } : {} },
    ),
  ).then(mapSatisfactionResults),

  // Trae TODOS los comentarios de la campaña (no solo la página visible) —
  // para exportar a CSV. El endpoint paginado (`getNpsComments`) tiene un
  // tope de page_size=100 en el backend, no sirve para esto.
  exportComments: (
    audience: Audience,
    campaignId: number,
    filters: NpsFilters = {},
  ): Promise<NpsComment[]> => unwrap(
    backendV2Service.get<{ items: NpsComment[] }>(
      `${base(audience)}/${campaignId}/comments/export`,
      { params: npsParams(filters) },
    ),
  ).then((data) => data.items),
};
